/**
 * A light segmented control for tabs INSIDE a tab.
 *
 * Deliberately not another PrimeReact TabView: nesting one inside the group
 * screen's TabView reads as two competing tab strips, and the inner one is a
 * smaller choice than the outer. A pill row says "these are views of the same
 * thing" where a second tab strip says "these are separate screens".
 *
 * The count is part of the control on purpose — the number waiting is the reason
 * someone clicks the tab at all.
 */
import { Icon, piIconName } from './icon';

export interface SubTab {
  key: string;
  label: string;
  icon?: string;
  /** shown as a pill when > 0; omit when the number is not worth watching */
  count?: number;
}

interface Props {
  tabs: SubTab[];
  active: string;
  onChange: (key: string) => void;
  testId?: string;
}

const SubTabs = ({ tabs, active, onChange, testId }: Props) => (
  <div className="inline-flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-900"
    data-testid={testId}>
    {tabs.map((t) => {
      const on = t.key === active;
      return (
        <button key={t.key} type="button" onClick={() => onChange(t.key)}
          data-testid={testId ? `${testId}.${t.key}` : undefined}
          className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${on
            ? 'bg-indigo-600 text-white shadow-sm'
            : 'text-gray-600 hover:bg-white hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white'}`}>
          {/* Same rule the outer tab strip follows: Lucide where the name maps,
              the PrimeIcon where it does not. */}
          {piIconName(t.icon)
            ? <Icon name={piIconName(t.icon)!} size={14} />
            : (t.icon && <i className={`${t.icon} text-xs`} />)}
          {t.label}
          {!!t.count && (
            <span className={`rounded-full px-1.5 text-[11px] font-bold ${on
              ? 'bg-white/25 text-white'
              : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
              {t.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export default SubTabs;
